// V4-FRONT-ANY-USAGE batch 4 2026-05-28
import { useState, useEffect, useRef, lazy, Suspense, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, X, Loader2 } from 'lucide-react';
import { PostsContext } from '../../contexts/PostsContext';
import { mergePostsById } from '../../utils/postLists';
import { Sidebar } from '../layout/Sidebar';
import { PostGallery } from '../features/posts/PostGallery';
import type { MobileHeaderControls } from '../features/posts/gallery/usePostFilters';
import { QueueSidebar } from '../layout/QueueSidebar';
// V4-CODESPLIT-DASHBOARD 2026-07-29 — les deux modales les plus lourdes sortent
// du chunk d'entrée. Elles ne sont jamais nécessaires au premier rendu du
// dashboard : GenerateModal s'ouvre sur clic, ExpandedPostModal sur sélection
// d'un post.
const GenerateModal = lazy(() =>
  import('../features/generation/GenerateModal').then(m => ({ default: m.GenerateModal }))
);
const ExpandedPostModal = lazy(() =>
  import('../features/posts/ExpandedPostModal').then(m => ({ default: m.ExpandedPostModal }))
);
import { MobileBurgerMenu } from '../layout/MobileBurgerMenu';
import { OnboardingTour } from '../features/onboarding/OnboardingTour';
import { OnboardingProgressWidget } from '../common/OnboardingProgressWidget';
import { useProductTour } from '../../hooks/useProductTour';
import { PaymentAlertBanner } from '../features/billing/PaymentAlertBanner';
import { MobileBottomBar } from '../layout/MobileBottomBar';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useRaisoData } from '../../hooks/useRaisoData';
import { useAuth } from '../../hooks/useAuth';
import { useReadOnly } from '../../hooks/useReadOnly';
import { useUrlSyncedTab } from '../../hooks/useUrlSyncedTab';
import { useGenerationContext } from '../../contexts/GenerationContext';
import { getAgentDisplayName, AGENT_ICONS, getChannelDisplayConfig, normalizeToChannelType, GENERATION_STATUS_CONFIG, ChannelSvgIcon } from '../../utils/Constants';
import { getPipelineSteps, AGENT_STEP, type PipelineStep } from '../../utils/pipelineSteps';
import { useLanguage } from '../../contexts/LanguageContext';
import type { PlatformProgress } from '../../hooks/useGeneration';
import type { Post, CommunicationChannel } from '../../types';
import type { GenerationProgressState } from '../../hooks/useGeneration';
import { Logger } from '../../utils/logger';


function MobileGenerationOverlay({
  progressState,
  onHide,
}: {
  progressState: GenerationProgressState;
  onHide: () => void;
}) {
  const { t } = useLanguage();
  const logRef = useRef<HTMLDivElement>(null);
  const [hoveredStep, setHoveredStep] = useState<number | null>(null);
  const steps = getPipelineSteps(t);
  const activeStep = AGENT_STEP[progressState.currentAgent ?? ''] ?? -1;

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [progressState.messages, progressState.currentStep]);

  return (
    <div
      className="fixed inset-0 z-[21500] flex flex-col overflow-hidden"
      style={{ backgroundColor: 'var(--color-background)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-4 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--color-border)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#3EFFC0' }}
          />
          <span className="font-semibold text-sm" style={{ color: 'var(--color-text-primary)' }}>
            Génération en cours…
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
            {progressState.progress}%
          </span>
          <button
            onClick={onHide}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--color-surface-elevated)' }}
            title="Masquer (la génération continue)"
          >
            <X className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
          </button>
        </div>
      </div>

      {/* Progress bar — directly below header so % is visually linked */}
      <div className="px-4 pt-3 flex-shrink-0">
        <div
          className="rounded-full overflow-hidden"
          style={{ height: 4, backgroundColor: 'var(--color-border)' }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${progressState.progress}%`,
              backgroundColor: 'var(--color-primary)',
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      </div>

      {/* Pipeline — horizontal strip, same horizontal margin as other cards */}
      <div
        className="mx-4 mt-3 rounded-2xl flex-shrink-0"
        style={{
          backgroundColor: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-border)',
          overflow: 'visible',
          padding: '0 12px',
        }}
      >
        <div className="flex items-start justify-between py-4" style={{ overflow: 'visible' }}>
          {steps.map((step, i) => {
            const isDone = i < activeStep;
            const isActive = i === activeStep;
            const { Icon, color, label, tooltip } = step;

            return (
              <div key={i} className="flex items-start flex-1">
                <div
                  className="flex flex-col items-center gap-1 flex-shrink-0"
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setHoveredStep(i)}
                  onMouseLeave={() => setHoveredStep(null)}
                >
                  {/* Tooltip */}
                  {hoveredStep === i && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 8px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'var(--color-text-primary)',
                        color: 'var(--color-background)',
                        fontSize: 11,
                        lineHeight: 1.4,
                        padding: '6px 10px',
                        borderRadius: 8,
                        width: 160,
                        textAlign: 'center',
                        zIndex: 100,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      {tooltip}
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '5px solid transparent',
                          borderRight: '5px solid transparent',
                          borderTop: '5px solid var(--color-text-primary)',
                        }}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      border: `2px solid ${color}`,
                      background: isDone || isActive ? color : 'transparent',
                      transform: isActive ? 'scale(1.1)' : 'scale(1)',
                      boxShadow: isActive ? `0 0 0 3px ${color}33` : 'none',
                      transition: 'all 0.35s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'default',
                    }}
                  >
                    <Icon size={15} color={isDone || isActive ? 'white' : color} />
                  </div>
                  <span
                    style={{
                      fontSize: 8,
                      fontWeight: 500,
                      color: isDone || isActive ? color : 'var(--color-text-secondary)',
                      transition: 'color 0.35s ease',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    style={{
                      flex: 1,
                      height: 2,
                      marginTop: 17,
                      marginLeft: 3,
                      marginRight: 3,
                      borderRadius: 1,
                      background: isDone
                        ? `linear-gradient(to right, ${color}, ${steps[i + 1].color})`
                        : isActive
                        ? `linear-gradient(to right, ${color} 40%, var(--color-border) 40%)`
                        : 'var(--color-border)',
                      transition: 'background 0.5s ease',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Canaux — platform status chips */}
      {progressState.platforms && progressState.platforms.length > 0 && (
        <div className="mx-4 mt-3 flex-shrink-0">
          <div
            className="rounded-2xl p-3"
            style={{
              backgroundColor: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border)',
            }}
          >
            <span className="text-xs font-semibold block mb-2" style={{ color: 'var(--color-text-primary)' }}>
              Canaux
            </span>
            <div className="flex flex-wrap gap-2">
              {progressState.platforms.map((platform) => {
                const config = getChannelDisplayConfig(platform.platform);
                const status = GENERATION_STATUS_CONFIG[platform.status] || GENERATION_STATUS_CONFIG.pending;
                const isGen = platform.status === 'generating';
                const channelType = normalizeToChannelType(platform.platform);
                return (
                  <div
                    key={platform.channelId}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${isGen ? 'animate-pulse' : ''}`}
                    style={{
                      backgroundColor: config.bgColor,
                      color: config.color,
                      border: `1px solid ${isGen ? config.color : 'transparent'}`,
                    }}
                  >
                    <ChannelSvgIcon network={channelType} size={13} />
                    <span>{config.label}</span>
                    <span className={isGen ? 'animate-spin' : ''} style={{ color: status.color }}>
                      {isGen ? <Loader2 className="w-3 h-3" /> : status.emoji}
                    </span>
                    {platform.status === 'completed' && (
                      <span className="text-[10px] opacity-75">
                        ({platform.postsGenerated}/{platform.postsRequested})
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Execution log — boxed like desktop */}
      <div className="mx-4 mt-3 mb-4 flex-1 min-h-0 flex flex-col rounded-2xl overflow-hidden"
        style={{
          backgroundColor: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-border)',
        }}
      >
        {/* Log header */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 flex-shrink-0">
          <span className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Exécution
          </span>
          <span
            className="w-2 h-2 rounded-full animate-pulse"
            style={{ backgroundColor: '#3EFFC0' }}
          />
        </div>

        {/* Log messages */}
        <div
          ref={logRef}
          className="flex-1 overflow-y-auto px-4 pb-4 space-y-1"
        >
          {progressState.messages.length === 0 ? (
            progressState.currentStep ? (
              <div className="flex items-start gap-2" style={{ padding: '4px 0' }}>
                <span style={{ fontSize: 14 }}>
                  {AGENT_ICONS[progressState.currentAgent] || '🤖'}
                </span>
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {getAgentDisplayName(progressState.currentAgent)}
                  </span>
                  {' '}{progressState.currentStep}
                </span>
              </div>
            ) : (
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Initialisation…
              </p>
            )
          ) : (
            progressState.messages.slice(-20).map((msg, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 rounded-lg"
                style={{
                  backgroundColor: ['compliance', 'diversity'].includes(msg.agent)
                    ? 'rgba(85,0,255,0.06)'
                    : 'transparent',
                  padding: '4px 8px',
                }}
              >
                <span className="flex-shrink-0 mt-0.5">
                  {msg.status === 'completed' ? (
                    <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--color-success)' }} />
                  ) : msg.status === 'error' ? (
                    <AlertCircle className="w-3.5 h-3.5" style={{ color: 'var(--color-danger)' }} />
                  ) : (
                    <span style={{ fontSize: 13 }}>{AGENT_ICONS[msg.agent] || '🤖'}</span>
                  )}
                </span>
                <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                    {getAgentDisplayName(msg.agent)}
                  </span>
                  {' '}{msg.message}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const isMobile = useIsMobile(768);
  const { user, currentSpace, isLoading: isAuthLoading } = useAuth();
  const readOnly = useReadOnly();
  const { t } = useLanguage();
  const { isGenerating, progressState, cancelGeneration, showGenerationOverlay, setShowGenerationOverlay } = useGenerationContext();
  
  useEffect(() => {
    Logger.debug('isMobile state:', isMobile, 'window.innerWidth:', window.innerWidth);
  }, [isMobile]);

  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [queueSidebarExpanded, setQueueSidebarExpanded] = useState(false);
  const [queueCloseKey, setQueueCloseKey] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSidebarTab, setActiveSidebarTab] = useState<string | null>(null);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);

  // Fix backlog 1.2 (19-05) : sync activeSidebarTab ↔ URL pour deeplinking
  // + support back/forward navigateur sans installer react-router-dom.
  useUrlSyncedTab(activeSidebarTab, setActiveSidebarTab);

  // Quand un tab devient actif (deeplink ou clic), expand la sidebar / ouvre
  // le burger mobile pour que le drawer s'affiche réellement.
  useEffect(() => {
    if (!activeSidebarTab) return;
    if (isMobile) {
      if (!mobileMenuOpen) setMobileMenuOpen(true);
    } else {
      if (!sidebarExpanded) setSidebarExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSidebarTab]);

  // Function to programmatically open a specific tab in the sidebar
  const handleOpenSidebarTab = (tabId: string) => {
    if (isMobile) {
      // Pour mobile, on s'assure que le menu burger est ouvert et on ouvre l'onglet
      setMobileMenuOpen(true);
      setActiveSidebarTab(tabId);
    } else {
      // Pour desktop — fermer QueueSidebar si ouvert
      setSidebarExpanded(true);
      setActiveSidebarTab(tabId);
      setQueueCloseKey(k => k + 1);
    }
  };
  
  const handleOpenBurger = () => {
    setMobileMenuOpen(true);
  };

  // Use the custom hook for data management
  const {
    posts,
    queue,
    allDatedPosts,
    newsletterPosts,
    sources,
    watchTopics,
    communicationChannels,
    droppedPostForScheduling,
    setDroppedPostForScheduling,
    automationSettings,
    setAutomationSettings,
    systemPrompt,
    setSystemPrompt,
    logoUrl,
    setLogoUrl,
    newsletterTemplate,
    setNewsletterTemplate,
    isGenerateModalOpen,
    setIsGenerateModalOpen,
    addToQueue,
    updatePost,
    unschedulePost,
    deletePost,
    generateNewPosts,
    createPost,
    handleCreatePost,
    duplicatePost,
    addSource,
    updateSource,
    deleteSource,
    refreshSources,
    enrichingTopicIds,
    addWatchTopic,
    updateWatchTopic,
    deleteWatchTopic,
    addCommunicationChannel,
    updateCommunicationChannel,
    deleteCommunicationChannel,
    loadChannels,
    addToNewsletter,
    removeFromNewsletter,
    scheduleNewsletter,
    isPositioningTemplate,
    sectorWatchEnabled,
    isDataLoading,
    refreshData,
    refreshPosts,
    updateAutomationSettings,
    publishPostNow
  } = useRaisoData();

  // State for mobile header controls
  // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — type partagé avec usePostFilters
  const [mobileHeaderControls, setMobileHeaderControls] = useState<MobileHeaderControls | null>(null);

  // V4-ONBOARDING-TOUR 2026-06-18 — tour produit premier login. Démarre quand le
  // dashboard est prêt (données chargées, aucun overlay/modale ouvert, sidebar fermée)
  // pour que les cibles soient visibles. Jamais pour un guest (lecture seule).
  const { run: tourRun, markSeen: markTourSeen } = useProductTour({
    userId: user?.id,
    ready: !isAuthLoading
      && !isDataLoading
      && !readOnly
      && !!currentSpace?.id
      && !isPostModalOpen
      && !isGenerateModalOpen
      && !activeSidebarTab
      && !mobileMenuOpen,
  });

  if (isAuthLoading) {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  const handleUpdateAutomationSettings = (settings: typeof automationSettings) => {
    setAutomationSettings(settings);
    if (currentSpace?.id) {
      updateAutomationSettings(currentSpace.id, settings);
    }
  };

  const postsContextValue = {
    posts,
    queue,
    createPost,
    deletePost,
    unschedulePost,
    updatePost,
    duplicatePost,
    addToQueue,
    addToNewsletter,
    refreshPosts,
  };

  return (
    <PostsContext.Provider value={postsContextValue}>
    <>
      {/* V4-MOBILE-NAV-1 : suppression du double rendu de MobileBurgerMenu.
          L'instance ci-dessous (avec activeTab/onTabChange et tous les props)
          est rendue plus bas. Avoir deux portails sur document.body créait des
          overlays superposés et le mauvais panneau apparaissait lors du tap
          sur le bouton burger en E2E. */}

      {/* V4-MOBILE-PADDING-BOTTOM 2026-05-29 — la MobileBottomBar floate fixed
          en bas (~84px). PostGalleryGrid a déjà `pb-24` interne, mais quelques
          écrans périphériques (wizard, états vides centrés en calc(100vh-…))
          peuvent passer sous la barre. On garde le ratio sûr ici via une
          variable CSS exposée (--mobile-bottom-safe) que les vues qui en ont
          besoin peuvent consommer. Cf. FINDING-MOBILE-01. */}
      <div
        className="h-screen w-screen overflow-hidden flex bg-[var(--color-surface)]"
        style={isMobile ? ({ '--mobile-bottom-safe': '96px' } as CSSProperties) : undefined}
      > {/* Inverted: surface (previously background) */}
        
        {/* Desktop: Sidebar gauche */}
        {!isMobile && (
          <Sidebar
            user={user}
            currentSpace={currentSpace}
            expanded={sidebarExpanded} 
            activeTab={activeSidebarTab}
            onTabChange={setActiveSidebarTab}
            onToggle={() => {
              const opening = !sidebarExpanded;
              setSidebarExpanded(opening);
              // Fermer le menu de droite si on ouvre celui de gauche
              if (opening) {
                setQueueSidebarExpanded(false);
                setQueueCloseKey(k => k + 1);
              }
            }}
            sources={sources}
            onAddSource={addSource}
            onUpdateSource={updateSource}
            onDeleteSource={deleteSource}
            onRefreshSources={refreshSources}
            watchTopics={watchTopics}
            enrichingTopicIds={enrichingTopicIds}
            onAddTopic={addWatchTopic}
            onUpdateTopic={updateWatchTopic}
            onDeleteTopic={deleteWatchTopic}
            communicationChannels={communicationChannels}
            onAddCommunicationChannel={addCommunicationChannel}
            onUpdateCommunicationChannel={updateCommunicationChannel}
            onDeleteCommunicationChannel={deleteCommunicationChannel}
            systemPrompt={systemPrompt}
            isPositioningTemplate={isPositioningTemplate}
            sectorWatchEnabled={sectorWatchEnabled}
            onUpdateSystemPrompt={setSystemPrompt}
            automationSettings={automationSettings}
            onUpdateAutomationSettings={handleUpdateAutomationSettings}
            newsletterTemplate={newsletterTemplate}
            onUpdateNewsletterTemplate={setNewsletterTemplate}
            queuePosts={queue}
            allPosts={mergePostsById(posts, queue)}
            logoUrl={logoUrl}
            onUpdateLogoUrl={setLogoUrl}
          />
        )}
        
        {/* Main content area - Gallery takes full height */}
        <div className={`flex-1 flex flex-col overflow-hidden ${!isMobile ? 'ml-16 mr-16' : ''}`}>
          {/* Bandeau alerte paiement échoué — invisible pour les beta users [BETA] */}
          <PaymentAlertBanner />
          <div className="flex-1 overflow-hidden"> {/* Removed padding to let gallery go full height */}
            <PostGallery
              onOpenGenerateModal={() => { if (!readOnly) setIsGenerateModalOpen(true); }}
              communicationChannels={communicationChannels}
              onCardLayoutChange={(_width, _gap) => {
                // Layout changes handled internally by PostGallery
              }}
              onOpenBurger={handleOpenBurger}
              logoUrl={logoUrl}
              isBurgerOpen={mobileMenuOpen}
              onHeaderControlsReady={setMobileHeaderControls}
              onOpenSidebarTab={handleOpenSidebarTab}
              systemPrompt={systemPrompt}
              isPositioningTemplate={isPositioningTemplate}
            sectorWatchEnabled={sectorWatchEnabled}
              watchTopics={watchTopics}
              sources={sources}
              onRefreshData={refreshData}
              isDataLoading={isDataLoading}
              onPublishNow={publishPostNow}
              onPostModalOpenChange={setIsPostModalOpen}
            />
          </div>
        </div>

        {/* Queue Sidebar - Right side - Desktop only */}
        {!isMobile && (
          <QueueSidebar
            expanded={queueSidebarExpanded}
            onToggle={() => {
              const opening = !queueSidebarExpanded;
              setQueueSidebarExpanded(opening);
              if (opening) {
                setSidebarExpanded(false);
                setActiveSidebarTab(null);
              }
            }}
            onOpen={() => {
              setSidebarExpanded(false);
              setActiveSidebarTab(null);
            }}
            closeKey={queueCloseKey}
            queue={queue}
            allDatedPosts={allDatedPosts}
            newsletterPosts={newsletterPosts}
            onAddToQueue={addToQueue}
            onAddToNewsletter={addToNewsletter}
            onUpdatePost={updatePost}
            onUnschedule={unschedulePost}
            onRemoveFromNewsletter={removeFromNewsletter}
            onDeletePost={deletePost}
            communicationChannels={communicationChannels}
            onDroppedPostForScheduling={setDroppedPostForScheduling}
            onScheduleNewsletter={scheduleNewsletter}
            allPosts={posts}
            onPublishNow={publishPostNow}
            // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — verrouille la queue/agenda tant qu'aucun post
            locked={posts.length === 0}
            lockedTooltip={t.onboarding.lockedPublicationsTooltip}
          />
        )}

        {/* Mobile: Bottom bar — hidden when a post modal is open */}
        {isMobile && !isPostModalOpen && (
          <MobileBottomBar
            queue={queue}
            newsletterPosts={newsletterPosts}
            onAddToNewsletter={addToNewsletter}
            onUpdatePost={updatePost}
            onUnschedule={unschedulePost}
            onRemoveFromNewsletter={removeFromNewsletter}
            onDeletePost={deletePost}
            communicationChannels={communicationChannels}
            onScheduleNewsletter={scheduleNewsletter}
            allPosts={posts}
            logoUrl={logoUrl}
            isBurgerOpen={mobileMenuOpen}
            onBurgerToggle={() => setMobileMenuOpen(prev => !prev)}
            // Header panel props from PostGallery
            searchQuery={mobileHeaderControls?.searchQuery}
            onSearchQueryChange={mobileHeaderControls?.onSearchChange}
            sortType={mobileHeaderControls?.sortType as 'date' | 'channel' | 'alphabetical'}
            sortOrder={mobileHeaderControls?.sortOrder}
            onSortTypeChange={mobileHeaderControls?.onSortTypeChange as ((type: 'date' | 'channel' | 'alphabetical') => void) | undefined}
            onSortOrderChange={mobileHeaderControls?.onSortOrderChange}
            statusFilter={mobileHeaderControls?.statusFilter}
            onStatusFilterChange={mobileHeaderControls?.onStatusFilterChange}
            onCreatePost={mobileHeaderControls?.onCreatePost}
            onGeneratePosts={mobileHeaderControls?.onGeneratePosts}
            onClearGallery={mobileHeaderControls?.onClearGallery}
            availableChannels={mobileHeaderControls?.availableChannels}
            selectedChannelIds={mobileHeaderControls?.selectedChannelIds}
            onToggleChannel={mobileHeaderControls?.onToggleChannel}
            showExternal={mobileHeaderControls?.showExternal}
            showInternal={mobileHeaderControls?.showInternal}
            onToggleExternal={mobileHeaderControls?.onToggleExternal}
            onToggleInternal={mobileHeaderControls?.onToggleInternal}
            showOnlyMine={mobileHeaderControls?.showOnlyMine}
            onToggleOnlyMine={mobileHeaderControls?.onToggleOnlyMine}
            canFilterMine={mobileHeaderControls?.canFilterMine}
            startDate={mobileHeaderControls?.startDate}
            endDate={mobileHeaderControls?.endDate}
            onStartDateChange={mobileHeaderControls?.onStartDateChange}
            onEndDateChange={mobileHeaderControls?.onEndDateChange}
            // New props for Create and Generate
            onCreateComplete={mobileHeaderControls?.onCreateComplete}
            onGenerateComplete={mobileHeaderControls?.onGenerateComplete}
          />
        )}

        {/* Mobile: Burger menu as separate portal */}
        {isMobile && (
          <MobileBurgerMenu
            sources={sources}
            onAddSource={addSource}
            onUpdateSource={updateSource}
            onDeleteSource={deleteSource}
            onRefreshSources={refreshSources}
            watchTopics={watchTopics}
            enrichingTopicIds={enrichingTopicIds}
            onAddTopic={addWatchTopic}
            onUpdateTopic={updateWatchTopic}
            onDeleteTopic={deleteWatchTopic}
            communicationChannels={communicationChannels}
            onAddCommunicationChannel={addCommunicationChannel}
            onUpdateCommunicationChannel={updateCommunicationChannel}
            onDeleteCommunicationChannel={deleteCommunicationChannel}
            onRefreshChannels={loadChannels}
            systemPrompt={systemPrompt}
            // V4-MOBILE-MENU-POSITIONING-TEMPLATE 2026-05-29 — parité desktop.
            isPositioningTemplate={isPositioningTemplate}
            sectorWatchEnabled={sectorWatchEnabled}
            onUpdateSystemPrompt={setSystemPrompt}
            automationSettings={automationSettings}
            onUpdateAutomationSettings={handleUpdateAutomationSettings}
            newsletterTemplate={newsletterTemplate}
            onUpdateNewsletterTemplate={setNewsletterTemplate}
            queuePosts={queue}
            allPosts={mergePostsById(posts, queue)}
            logoUrl={logoUrl}
            onUpdateLogoUrl={setLogoUrl}
            isOpen={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
            user={user}
            currentSpace={currentSpace}
            activeTab={activeSidebarTab}
            onTabChange={setActiveSidebarTab}
          />
        )}

        {/* ⚠️ `|| isGenerating` est OBLIGATOIRE, pas une optimisation de confort :
            `useGeneration()` détient le WebSocket de génération dans une ref
            d'instance, et son cleanup de démontage ne le ferme pas. Démonter la
            modale pendant une génération orphelinerait la socket — la
            progression cesserait de remonter et les posts produits
            n'atterriraient jamais dans la galerie. L'utilisateur PEUT fermer la
            modale pendant une génération (cf. l'indicateur flottant plus bas),
            donc ce cas est nominal, pas marginal. */}
        {!readOnly && (isGenerateModalOpen || isGenerating) && (
          <Suspense fallback={null}>
            <GenerateModal
              open={isGenerateModalOpen}
              onClose={() => setIsGenerateModalOpen(false)}
              onGenerate={generateNewPosts}
              communicationChannels={communicationChannels}
              onOpenSidebarTab={handleOpenSidebarTab}
            />
          </Suspense>
        )}

        {/* Phase 1.4 — checklist de progression globale persistante.
            Visible sur toutes les pages tant que le setup n'est pas terminé,
            avec reprise « Vous en étiez à l'étape X ». Se masque seule quand le
            setup est complet ou pour un lecteur (cf. OnboardingProgressWidget).
            V4-FRONT-WIZARD-REACCESS 2026-07-11 — parité mobile : le widget est
            désormais rendu aussi sur mobile (ancré au-dessus de la MobileBottomBar,
            replié par défaut), plus seulement en desktop. */}
        <OnboardingProgressWidget
          systemPrompt={systemPrompt}
          isPositioningTemplate={isPositioningTemplate}
          sectorWatchEnabled={sectorWatchEnabled}
          watchTopics={watchTopics}
          sources={sources}
          communicationChannels={communicationChannels}
          posts={mergePostsById(posts, queue)}
          onOpenSidebarTab={handleOpenSidebarTab}
          isMobile={isMobile}
        />

        {/* V4-ONBOARDING-TOUR 2026-06-18 — bulles explicatives premier login.
            Parcours USER vs AGENCY, desktop vs mobile (cf. OnboardingTour). */}
        {!readOnly && (
          <OnboardingTour
            run={tourRun}
            onFinish={markTourSeen}
            accountType={user?.accountType}
            isMobile={isMobile}
          />
        )}

        {/* Global modal for dropped post scheduling */}
        {droppedPostForScheduling && (
          <Suspense fallback={null}>
          <ExpandedPostModal
            post={droppedPostForScheduling}
            onClose={() => setDroppedPostForScheduling(null)}
            onAddToQueue={addToQueue}
            onPublishNow={publishPostNow}
            communicationChannels={communicationChannels}
            openDatePickerOnMount={true}
            queuePosts={queue}
          />
          </Suspense>
        )}
      </div>

      {/* Global mobile generation progress overlay — dismissible, persists regardless of panel state */}
      {isMobile && isGenerating && progressState && showGenerationOverlay && createPortal(
        <MobileGenerationOverlay
          progressState={progressState}
          onHide={() => setShowGenerationOverlay(false)}
        />,
        document.body
      )}

      {/* V4-UX-GEN-CANCEL-DESKTOP 2026-07-02 — pastille flottante desktop quand la
          GenerateModal est fermée pendant une génération : avant, plus aucun indicateur
          ni moyen d'annuler (cancelGeneration n'était exposé que dans la modale et
          l'overlay mobile). Clic sur la pastille → rouvre la modale (vue progression) ;
          bouton X → annule. Inline styles : Tailwind figé (V4-FRONT-TAILWIND-FROZEN-CSS). */}
      {!isMobile && isGenerating && progressState && !isGenerateModalOpen && createPortal(
        <div
          className="fixed flex items-center gap-3"
          style={{
            bottom: 24,
            right: 24,
            zIndex: 21500,
            backgroundColor: 'var(--color-surface-elevated, var(--color-surface))',
            border: '1px solid var(--color-border)',
            borderRadius: 999,
            padding: '10px 14px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          }}
        >
          <button
            onClick={() => setIsGenerateModalOpen(true)}
            className="flex items-center gap-2 cursor-pointer"
            style={{ background: 'none', border: 'none', padding: 0 }}
            title={t.generate.viewProgress}
          >
            <span
              className="w-2 h-2 rounded-full animate-pulse flex-shrink-0"
              style={{ backgroundColor: '#3EFFC0' }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {t.generate.inProgress}
            </span>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-primary)' }}>
              {progressState.progress}%
            </span>
          </button>
          {cancelGeneration && (
            <button
              onClick={cancelGeneration}
              className="w-7 h-7 rounded-full flex items-center justify-center cursor-pointer"
              style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}
              title={t.generate.cancel}
              aria-label={t.generate.cancel}
            >
              <X className="w-3.5 h-3.5" style={{ color: 'var(--color-danger)' }} />
            </button>
          )}
        </div>,
        document.body
      )}
    </>
    </PostsContext.Provider>
  );
}
